/**
 * Execution Engine Health Badge — L2 book sağlık göstergesi.
 *
 * /api/execution/status'ı 2sn'de bir poll eder ve renkli bir compact
 * rozette gösterir:
 *   - Yeşil: tüm book'lar healthy
 *   - Sarı: en az 1 stale
 *   - Kırmızı: hiç book yok / engine kapalı / network hatası
 *
 * Tıklanırsa popover ile per-exchange detay açar (en yaşlı 3 book).
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Activity, ChevronDown } from "lucide-react";
import { http } from "@/lib/api";

export default function ExecutionHealthBadge() {
  const [open, setOpen] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ["execution-status"],
    queryFn: () => http.get("/execution/status").then((r) => r.data),
    refetchInterval: 2000,
    retry: 1,
    staleTime: 1000,
  });

  let color = "#9ca3af";
  let bg = "rgba(156, 163, 175, 0.08)";
  let border = "rgba(156, 163, 175, 0.25)";
  let label = "—";

  if (error) {
    const code = error?.response?.status;
    if (code === 401 || code === 403) {
      // Engine değil, oturum sorunu — kullanıcıya doğru sinyal ver
      color = "#9ca3af";
      bg = "rgba(156, 163, 175, 0.08)";
      border = "rgba(156, 163, 175, 0.25)";
      label = "OTURUM SÜRESİ";
    } else {
      color = "#fb7185";
      bg = "rgba(251, 113, 133, 0.08)";
      border = "rgba(251, 113, 133, 0.3)";
      label = "ENGINE OFF";
    }
  } else if (data) {
    const { healthy, stale, total } = data;
    if (total === 0) {
      color = "#fb7185";
      bg = "rgba(251, 113, 133, 0.08)";
      border = "rgba(251, 113, 133, 0.3)";
      label = "NO BOOKS";
    } else if (stale === 0) {
      color = "#4ade80";
      bg = "rgba(74, 222, 128, 0.08)";
      border = "rgba(74, 222, 128, 0.3)";
      label = `${healthy}/${total} HEALTHY`;
    } else if (stale < total / 2) {
      color = "#fbbf24";
      bg = "rgba(251, 191, 36, 0.08)";
      border = "rgba(251, 191, 36, 0.3)";
      label = `${stale} STALE`;
    } else {
      color = "#fb7185";
      bg = "rgba(251, 113, 133, 0.08)";
      border = "rgba(251, 113, 133, 0.3)";
      label = `${stale}/${total} STALE`;
    }
  } else if (isLoading) {
    label = "…";
  }

  // En yaşlı 5 book — popover'da göster
  const oldestBooks = data?.books
    ? Object.entries(data.books)
        .map(([k, v]) => ({ key: k, ...v }))
        .sort((a, b) => (b.age_sec || 0) - (a.age_sec || 0))
        .slice(0, 5)
    : [];

  return (
    <div style={{ position: "relative", display: "inline-block" }}
         data-testid="exec-health-badge-wrap">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="exec-health-badge"
        className="exec-hb-pill"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          height: 28,
          padding: "0 10px",
          background: bg,
          border: `1px solid ${border}`,
          color,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: "0.15em",
          textTransform: "uppercase",
          fontFamily: "ui-monospace, SFMono-Regular, monospace",
          cursor: "pointer",
          transition: "background 120ms",
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: color,
            boxShadow: `0 0 8px ${color}`,
            animation: data && data.stale === 0 ? "exec-pulse 2s infinite" : "none",
          }}
        />
        <Activity size={11} />
        {label}
        <ChevronDown size={11} style={{
          transition: "transform 200ms",
          transform: open ? "rotate(180deg)" : "rotate(0)",
        }} />
      </button>

      {open && (
        <div
          data-testid="exec-health-popover"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            zIndex: 50,
            minWidth: 320,
            background: "#0a0a0b",
            border: "1px solid rgba(255,255,255,0.08)",
            padding: "12px 14px",
            fontFamily: "ui-sans-serif, system-ui, sans-serif",
          }}
        >
          <div style={{
            fontSize: 10,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: "#6b7280",
            marginBottom: 10,
          }}>
            Execution Engine — L2 Books
          </div>
          {data ? (
            <>
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 8,
                marginBottom: 12,
                paddingBottom: 10,
                borderBottom: "1px solid rgba(255,255,255,0.05)",
              }}>
                <Stat label="Total" value={data.total} />
                <Stat label="Healthy" value={data.healthy} color="#4ade80" />
                <Stat label="Stale" value={data.stale}
                      color={data.stale > 0 ? "#fb7185" : "#6b7280"} />
              </div>
              <div style={{
                fontSize: 9.5,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: "#6b7280",
                marginBottom: 6,
              }}>
                En Yaşlı Book'lar
              </div>
              {oldestBooks.map((b) => (
                <div
                  key={b.key}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "4px 0",
                    fontSize: 11.5,
                    fontFamily: "ui-monospace, monospace",
                  }}
                >
                  <span style={{ color: "#e5e7eb" }}>{b.key}</span>
                  <span style={{
                    color: b.stale ? "#fb7185" : "#9ca3af",
                    fontVariantNumeric: "tabular-nums",
                  }}>
                    {b.age_sec != null ? `${Math.round(b.age_sec * 1000)}ms` : "—"}
                    {b.stale && " ⚠"}
                  </span>
                </div>
              ))}
              {data.stale > 0 && (
                <div style={{
                  marginTop: 10,
                  paddingTop: 8,
                  borderTop: "1px solid rgba(255,255,255,0.05)",
                  fontSize: 10.5,
                  color: "#fbbf24",
                  letterSpacing: "0.05em",
                }}>
                  ⚠️ Stale book üzerinde işlem otomatik bloklanır.
                </div>
              )}
            </>
          ) : (
            <div style={{ color: "#6b7280", fontSize: 11 }}>
              {error ? "Engine status okunamadı" : "Yükleniyor…"}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes exec-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
        .exec-hb-pill:hover {
          background: rgba(255, 255, 255, 0.04) !important;
        }
      `}</style>
    </div>
  );
}

function Stat({ label, value, color = "#e5e7eb" }) {
  return (
    <div>
      <div style={{
        fontSize: 9.5,
        letterSpacing: "0.15em",
        textTransform: "uppercase",
        color: "#6b7280",
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 18,
        fontWeight: 600,
        color,
        fontFamily: "ui-monospace, monospace",
        fontVariantNumeric: "tabular-nums",
        marginTop: 2,
      }}>
        {value ?? "—"}
      </div>
    </div>
  );
}
